/* EDRE — IssueCard (STA-7 · Feb 2026)
   ==================================================================
   Renders the full 8-attribute contract for a diagnostic issue:
     1. Error code + type badge
     2. Severity chip (critical / warning / info / configuration)
     3. Location (page + section + record IDs) with deep-link
     4. What (business-language summary)
     5. Why (contributing causes list)
     6. Root cause (technical one-liner)
     7. Fix steps (numbered) with per-step action buttons
     8. Prevention statement
   Plus lifecycle controls: Preview · Execute · Verify · Close · Dismiss */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { axiosInstance } from '../../App';
import {
  AlertTriangle, ShieldAlert, Settings2, Info, CheckCircle2,
  ExternalLink, ArrowRight, Loader2, Lock, XCircle, Zap,
  MapPin, Compass, FileText, TrendingUp,
} from 'lucide-react';
import { Button } from '../ui/button';

const SEVERITY_META = {
  critical:      { cls: 'bg-rose-50 border-rose-300 text-rose-900',
                    chip: 'bg-rose-600', Icon: XCircle, label: 'حرِج' },
  warning:       { cls: 'bg-amber-50 border-amber-300 text-amber-900',
                    chip: 'bg-amber-500', Icon: AlertTriangle, label: 'تحذير' },
  configuration: { cls: 'bg-slate-50 border-slate-300 text-slate-800',
                    chip: 'bg-slate-500', Icon: Settings2, label: 'إعداد ناقص' },
  info:          { cls: 'bg-sky-50 border-sky-300 text-sky-900',
                    chip: 'bg-sky-500', Icon: Info, label: 'معلومة' },
};

const fmtSar = (n) => n === null || n === undefined
  ? '—'
  : Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function IssueCard({ issue, onResolved }) {
  const nav = useNavigate();
  const [running, setRunning] = useState(false);
  const [resolving, setResolving] = useState(false);
  const meta = SEVERITY_META[issue.severity] || SEVERITY_META.info;
  const { Icon } = meta;

  const executeFix = async () => {
    if (!issue.fix_execute_endpoint) return;
    if (!window.confirm(
      `تنفيذ الإصلاح؟\n\n${issue.what}\n\nالتأثير المالي: ${fmtSar(issue.impact?.financial_impact_sar)} SAR\nقيود جديدة: ${issue.impact?.creates_journals || 0}`)) return;
    setRunning(true);
    try {
      await axiosInstance.post(issue.fix_execute_endpoint);
      toast.success('تم تنفيذ الإصلاح — سيتم إعادة الفحص');
      onResolved?.(issue.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل تنفيذ الإصلاح');
    } finally { setRunning(false); }
  };

  const markResolved = async () => {
    if (!window.confirm('تأكيد إغلاق الحالة يدوياً؟')) return;
    setResolving(true);
    try {
      await axiosInstance.post(`/edre/issues/${issue.id}/resolve`);
      toast.success('تم إغلاق الحالة نهائياً — لن تُعاد فتحها');
      onResolved?.(issue.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل الإغلاق');
    } finally { setResolving(false); }
  };

  const runStep = (step) => {
    if (step.action === 'navigate' && step.target) {
      if (step.target.startsWith('/')) nav(step.target);
      else window.open(step.target, '_blank');
    } else if (step.action === 'one_click' && step.target) {
      // trigger the fix directly
      executeFix();
    }
  };

  return (
    <div className={`rounded-lg border-2 ${meta.cls} overflow-hidden`}
         data-testid={`issue-card-${issue.error_code}`}>
      {/* Header — Severity · Title · Location */}
      <div className="px-4 py-3 border-b border-current border-opacity-20 flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1">
          <Icon size={20} className="mt-0.5" />
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold text-white ${meta.chip}`}>
                {meta.label}
              </span>
              <span className="text-[10px] font-mono opacity-70 bg-white/60 px-1.5 py-0.5 rounded"
                    data-testid="issue-error-code">
                {issue.error_code}
              </span>
              {issue.variance_amount !== null && issue.variance_amount !== undefined && (
                <span className="text-[10px] font-mono opacity-80 bg-white/60 px-1.5 py-0.5 rounded">
                  فرق: {fmtSar(issue.variance_amount)} SAR
                </span>
              )}
            </div>
            <div className="font-bold text-sm mt-1">{issue.title_ar}</div>
          </div>
        </div>
        {issue.location?.deep_link && (
          <Button size="sm" variant="outline" onClick={() => nav(issue.location.deep_link)}
                   className="whitespace-nowrap"
                   data-testid="issue-deeplink-btn">
            <MapPin size={12} className="ml-1" /> اذهب لموقع الخطأ
          </Button>
        )}
      </div>

      {/* Body */}
      <div className="px-4 py-3 space-y-4 bg-white/60 text-slate-800">
        {/* WHAT */}
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
            🎯 ماذا حصل (WHAT)
          </div>
          <p className="text-sm leading-relaxed">{issue.what}</p>
        </div>

        {/* WHERE */}
        {issue.location && (
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
              📍 أين المشكلة (WHERE)
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
              {issue.location.page && <LocationCell label="الصفحة" value={issue.location.page} />}
              {issue.location.section && <LocationCell label="القسم" value={issue.location.section} />}
              {issue.location.collection && <LocationCell label="Table" value={issue.location.collection} mono />}
              {issue.location.period && <LocationCell label="الفترة" value={issue.location.period} />}
              {issue.location.record_id && <LocationCell label="Record" value={issue.location.record_id.slice(0, 12) + '...'} mono />}
              {issue.location.journal_no && <LocationCell label="Journal" value={issue.location.journal_no} mono />}
              {issue.location.invoice_no && <LocationCell label="Invoice" value={issue.location.invoice_no} mono />}
              {issue.location.bill_no && <LocationCell label="Bill" value={issue.location.bill_no} mono />}
            </div>
          </div>
        )}

        {/* WHY */}
        {issue.why?.length > 0 && (
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
              🔎 لماذا حصل (WHY)
            </div>
            <ul className="space-y-1 text-sm">
              {issue.why.map((r, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-slate-400 mt-0.5">•</span>
                  <span className="flex-1">{r}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* ROOT CAUSE */}
        {issue.root_cause && (
          <div className="bg-slate-100 border border-slate-200 rounded p-2">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
              ⚙️ السبب الجذري (Root Cause · Technical)
            </div>
            <p className="text-[11px] font-mono text-slate-700 leading-relaxed">{issue.root_cause}</p>
          </div>
        )}

        {/* IMPACT */}
        {issue.impact && (
          <div className="bg-amber-50 border border-amber-200 rounded p-2">
            <div className="text-[10px] font-bold uppercase tracking-wider text-amber-800 mb-1">
              ⚡ التأثير قبل الإصلاح (Impact Analysis)
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-1 text-[11px]">
              <ImpactCell active={issue.impact.affects_gl} label="GL" />
              <ImpactCell active={issue.impact.affects_vat} label="VAT" />
              <ImpactCell active={issue.impact.affects_bank} label="Bank" />
              <ImpactCell active={issue.impact.creates_journals > 0} label={`قيود جديدة: ${issue.impact.creates_journals}`} />
              <ImpactCell active={issue.impact.modifies_journals > 0} label={`تعديل قيود: ${issue.impact.modifies_journals}`} />
              <ImpactCell active={issue.impact.financial_impact_sar > 0} label={`مالياً: ${fmtSar(issue.impact.financial_impact_sar)}`} />
            </div>
            {issue.impact.notes && (
              <p className="text-[10px] mt-1 text-amber-900 leading-relaxed">{issue.impact.notes}</p>
            )}
          </div>
        )}

        {/* HOW / FIX STEPS */}
        {issue.fix_steps?.length > 0 && (
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">
              🛠️ كيف أصلحه (HOW · خطوات)
            </div>
            <ol className="space-y-2">
              {issue.fix_steps.map(s => (
                <li key={s.step}
                    className={`flex items-start gap-3 rounded p-2 ${s.critical ? 'bg-rose-50 border border-rose-200' : 'bg-slate-50 border border-slate-200'}`}
                    data-testid={`fix-step-${s.step}`}>
                  <span className={`flex-none w-6 h-6 rounded-full text-[11px] font-bold flex items-center justify-center ${s.critical ? 'bg-rose-600 text-white' : 'bg-slate-700 text-white'}`}>
                    {s.step}
                  </span>
                  <span className="flex-1 text-sm leading-relaxed">{s.text}</span>
                  {(s.action === 'navigate' || s.action === 'one_click') && (
                    <button
                      type="button"
                      onClick={() => runStep(s)}
                      className="text-[11px] bg-white border border-slate-300 hover:border-indigo-400 text-indigo-700 font-semibold rounded px-2 py-1 whitespace-nowrap flex items-center gap-1"
                      data-testid={`fix-step-action-${s.step}`}>
                      {s.action === 'one_click' ? <Zap size={11} /> : <ArrowRight size={11} />}
                      {s.action === 'one_click' ? 'نفِّذ الآن' : 'اذهب'}
                    </button>
                  )}
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* PREVENTION */}
        {issue.prevention && (
          <div className="bg-emerald-50 border border-emerald-200 rounded p-2">
            <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-800 mb-1">
              🔒 لماذا لن تتكرر (Prevention)
            </div>
            <p className="text-[11px] text-emerald-900 leading-relaxed">{issue.prevention}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-slate-200">
          <div className="text-[10px] text-slate-500">
            <span className="font-mono">Detected: {(issue.detected_at || '').slice(0, 16)}</span>
            {' · '}
            <span className="font-mono">Detector: {issue.detected_by}</span>
          </div>
          <div className="flex gap-2">
            {issue.fix_execute_endpoint && (
              <Button size="sm" variant="destructive" onClick={executeFix} disabled={running}
                       data-testid="issue-execute-fix">
                {running ? <Loader2 size={12} className="ml-1 animate-spin" /> : <Zap size={12} className="ml-1" />}
                نفِّذ الإصلاح تلقائياً
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={markResolved} disabled={resolving}
                    data-testid="issue-mark-resolved">
              {resolving ? <Loader2 size={12} className="ml-1 animate-spin" /> : <Lock size={12} className="ml-1" />}
              أغلق الحالة نهائياً
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function LocationCell({ label, value, mono = false }) {
  return (
    <div className="bg-white border border-slate-200 rounded p-1.5">
      <div className="text-[9px] uppercase text-slate-500 tracking-wider">{label}</div>
      <div className={`text-[11px] ${mono ? 'font-mono' : ''} text-slate-800`}>{value}</div>
    </div>
  );
}

function ImpactCell({ active, label }) {
  return (
    <div className={`px-2 py-1 rounded text-[10px] ${active ? 'bg-amber-200 text-amber-900 font-bold' : 'bg-white text-slate-500 border border-slate-200'}`}>
      {active ? '✓' : '○'} {label}
    </div>
  );
}
